import { useCallback, useContext } from 'react'

import {
  Context,
  AnnotationOverlayContextAvailable,
} from '../components/AnnotationOverlayContext'

import { MouseCoordinates, DraftAnnotationParameters } from '../types'
import { getDotPositioningFromMouseCoordinates } from '../utils'

/**
 * usePlaceDraftAnnotation
 *
 * Allows/Deny a annotation to place a draft annotation
 * when the status of the context is "available" it returns
 * the current value/setting of conceding a draft annotation
 * to be placed
 */
export const usePlaceDraftAnnotation = () => {
  const context = useContext(Context)

  if (context.status === 'available') {
    return [
      context.placeDraftAnnotation,
      context.setPlaceDraftAnnotation,
    ] as const
  }
}

export const useAnnotationOverlayContext = () => {
  const context = useContext(Context)

  if (context.status === 'available') {
    return context
  }
}
/**
 *
 * useCreateDraftAnnotation
 *
 * Saves the position of a draft annotation on the context
 * it also saves the context of the draft annotation like the artboard or the page
 */
export const useCreateDraftAnnotation = (params: DraftAnnotationParameters) => {
  const context = useContext(Context)

  return useCallback(
    (eventPosition: MouseCoordinates) => {
      const draftParameters = { ...params }

      /**
       * Make sure the context is available before continuing
       * and that "placeDraftAnnotation" is "true"
       */
      if (
        context.status === 'unavailable' ||
        !context.placeDraftAnnotation ||
        !context.origin.current ||
        !context.scale.current
      ) {
        return
      }

      const {
        position,
        prefixBounds,
        permanentArtboardId,
      } = getDotPositioningFromMouseCoordinates(
        eventPosition,
        context.origin.current,
        context.scale.current
      )

      if (permanentArtboardId) {
        draftParameters.permanentArtboardId = permanentArtboardId
      }

      context.addDraftAnnotation({
        context: draftParameters,
        coordinates: position,
        prefixBounds,
      })
    },
    [params, context]
  )
}

/**
 * useGetDraftAnnotation
 *
 * Gets the information of a draft annotation if
 * it has been set or if the context is available
 */
export const useGetDraftAnnotation = () => {
  const context = useContext(Context)

  if (context.status === 'available') {
    return context.draftAnnotation || undefined
  }
}

export const useGetAnnotationContext = () => {
  return useContext(Context) as Partial<AnnotationOverlayContextAvailable>
}

/**
 * useGetDraftAnnotationComment
 *
 * Gets the information of a draft annotation comment if
 * it has been set or if the context is available
 */
export const useGetDraftAnnotationComment = () => {
  const context = useContext(Context)

  if (context.status === 'available') {
    return [
      context.draftAnnotationComment,
      context.setDraftAnnotationComment,
    ] as const
  }

  return []
}

/**
 * useIsAnnotationAvailable
 *
 * Returns if the context is available or not
 */
export const useIsAnnotationContextAvailable = () => {
  const context = useContext(Context)

  return context?.status === 'available'
}

export const useMovingAnnotation = () => {
  const context = useContext(Context)

  if (context.status === 'available') {
    return [context.movingAnnotation, context.setMovingAnnotation] as const
  }
}

export const useAnnotationOverlayOrigin = () => {
  const context = useContext(Context)
  const isContextAvailable = context.status === 'available'

  const {
    origin: contextOrigin,
    scale: contextScale,
  } = context as Partial<AnnotationOverlayContextAvailable>

  const setOverlayOrigin = useCallback(
    (origin: React.RefObject<HTMLElement | null>, scale: number) => {
      if (!isContextAvailable) {
        return
      }

      if (contextOrigin) {
        // eslint-disable-next-line no-extra-semi
        ;(contextOrigin.current as HTMLElement | null) = origin.current
      }

      if (contextScale) {
        // eslint-disable-next-line no-extra-semi
        ;(contextScale.current as number) = scale
      }
    },
    [contextOrigin, contextScale, isContextAvailable]
  )

  return [
    { origin: contextOrigin, scale: contextScale },
    setOverlayOrigin,
  ] as const
}
